'use client'

import { cn, formatUSD } from '@/lib/utils'
import { Button, buttonVariants } from '@components/button'
import { gridItemTransition } from '@components/grid'
import { ALLOWED_LIST_PLATFORMS, getInvocationFromTokenId } from '@utils/common'
import { BodyText, DetailText } from 'design-configuration/components'
import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { motion, AnimatePresence } from 'motion/react'
import { useRef, useState } from 'react'
import { AllPurposeLink } from '@components/all-purpose-link'
import { SoloViewerTokenNoProjectFragment } from '@/gql/generated/graphql'
import { createSerializer, inferParserType, parseAsBoolean } from 'nuqs'
import { tokenFilterParsers } from './utils'
import { tokenShowRoute } from '@utils/routes'
import { BidModal, BuyModal, ListModal } from '@reservoir0x/reservoir-kit-ui'
import { getReservoirCollectionId, getReservoirTokenId } from '@utils/reservoir'
import { useSession } from 'next-auth/react'
import { env } from 'environments'
import { trackButtonClicked, TrackedButtonNames } from '@utils/analytics'
import { useSecondaryBuyerModalState } from '@/hooks/use-secondary-buyer-modal-state'
import { useETHToUSD } from '@/hooks/use-usd'
import { useSecondaryOwnerModalState } from '@/hooks/use-secondary-owner-modal-state'

export const ProjectTokenGridItem = ({
  token,
  searchActive,
  showListing,
  aspectRatio,
  searchParams,
  hideNavigationArrows = false
}: {
  token: SoloViewerTokenNoProjectFragment
  searchActive?: boolean
  showListing?: boolean
  onClick?: () => void
  aspectRatio?: number
  searchParams?: inferParserType<typeof tokenFilterParsers>
  hideNavigationArrows?: boolean
}): React.ReactElement => {
  const { data: session } = useSession()
  const isOwner = session?.address?.toLowerCase() === token.owner_address
  const [isHovered, setIsHovered] = useState(false)

  const isRendered = !!token.image_id
  const contractAddress = token.id.split('-')[0]
  const invocation = getInvocationFromTokenId(token.token_id)
  const currencySymbol = token.list_currency_symbol === 'ETH' ? 'Ξ' : token.list_currency_symbol
  const listPrice =
    token.list_price && ALLOWED_LIST_PLATFORMS.includes(token.list_platform ?? '') ? token.list_price : null

  const { usd: listPriceUsd } = useETHToUSD(listPrice ?? 0)

  const tokenParamsSerializer = createSerializer({
    ...tokenFilterParsers,
    hideNavigationArrows: parseAsBoolean.withDefault(false)
  })

  return (
    <motion.div
      className={`relative ${isRendered ? 'h-fit' : 'h-full'} flex w-full flex-col`}
      id={token.id}
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      // onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      transition={gridItemTransition}
    >
      <AnimatedAllPurposeLink
        layout
        href={tokenParamsSerializer(tokenShowRoute(contractAddress, token.token_id), {
          ...searchParams,
          ...(hideNavigationArrows && { hideNavigationArrows: true })
        })}
        className="relative isolate z-10 block flex-1"
        onClick={() => {
          trackButtonClicked(TrackedButtonNames.tokenGridItem, {
            token: token,
            customProperties: {
              Location: 'Project Token Grid Item'
            }
          })
        }}
      >
        {isRendered ? (
          token.video_id ? (
            <TokenGridVideo
              videoSrc={token.media_url?.replace('.png', '.mp4') ?? ''}
              imageSrc={token.media_url ?? ''}
              blurHash={token.image?.metadata?.blurhash}
              aspectRatio={aspectRatio ?? 1}
            />
          ) : (
            <div className="relative w-full">
              <ImageWithCustomLoader
                src={token.media_url ?? ''}
                style={{
                  objectFit: 'contain',
                  objectPosition: 'center',
                  width: '100%',
                  height: '100%'
                }}
                priority
                alt={`${token.token_id}`}
                sizes="33vw"
                blurHash={token.image?.metadata?.blurhash}
                width={0}
                height={0}
                className="shadow-artwork h-full w-full"
              />
            </div>
          )
        ) : (
          <div
            className={cn(
              `bg-surface-container gap-y-xs flex h-full min-h-[150px] w-full flex-col items-center justify-center`
            )}
          >
            <DetailText className="font-medium">{`Rendering #${getInvocationFromTokenId(token.token_id)}`}</DetailText>
            <Button colorTheme="secondary">
              <BodyText>Live View</BodyText>
            </Button>
          </div>
        )}

        <AnimatePresence>
          {(isHovered || searchActive) && isRendered && !showListing ? (
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="left-xs bottom-xs bg-primary-80 p-xs rounded-xs absolute z-10 flex items-center justify-center backdrop-blur-sm"
            >
              <DetailText className="text-on-primary font-medium">{`#${invocation}`}</DetailText>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </AnimatedAllPurposeLink>
      <AnimatePresence>
        {showListing ? (
          <motion.div
            key={'listing-data'}
            // Animate height and padding instead of transform to work nicely with parent layout animation
            exit={{
              height: 0,
              paddingTop: 0,
              paddingBottom: 0
            }}
            transition={{
              visualDuration: 0.3,
              bounce: 0
            }}
            className="px-sm py-sm bg-on-primary border-outline-variant rounded-xs text-body relative -top-[2px] flex items-start justify-between overflow-hidden border font-medium"
          >
            <span>{`#${invocation}`}</span>
            {listPrice ? (
              <span className="flex items-center gap-x-2">
                <span className="gap-2xs flex flex-col items-end">
                  <span>{`${listPrice.toString()} ${currencySymbol}`}</span>
                  <DetailText className="text-on-surface-variant font-normal">{`$${formatUSD(listPriceUsd)} USD`}</DetailText>
                </span>
              </span>
            ) : (
              <span className="gap-2xs flex flex-col items-end">
                <span>--</span>
                <DetailText className="text-on-surface-variant font-normal">Not listed</DetailText>
              </span>
            )}
            <motion.div
              className="absolute bottom-0 left-0 right-0 top-2"
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
                y: isHovered ? 0 : 'calc(100% + 10px)',
                transition: { opacity: { delay: 0.2 }, y: { duration: 0.2 } }
              }}
              transition={{ visualDuration: 0.2, bounce: 0 }}
            >
              {/* Background becomes transparent when hovered so add a white background to hide what's behind */}
              <div className="absolute inset-0 -z-10 bg-white" />
              {isOwner ? (
                <SecondaryOwnerActions
                  listPrice={listPrice}
                  token={token}
                  tokenRoute={tokenParamsSerializer(tokenShowRoute(token.contract_address, token.token_id), {
                    ...searchParams,
                    ...(hideNavigationArrows && { hideNavigationArrows: true })
                  })}
                />
              ) : (
                <SecondaryBuyerActions listPrice={listPrice} token={token} />
              )}
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </motion.div>
  )
}

const SecondaryBuyerActions = ({
  listPrice,
  token
}: {
  listPrice: number | null
  token: SoloViewerTokenNoProjectFragment
}) => {
  const { buyModalOpen, openBuyModal, closeBuyModal, openBidModal, closeBidModal, bidModalOpen } =
    useSecondaryBuyerModalState({
      token
    })
  const currencySymbol = token.list_currency_symbol === 'ETH' ? 'Ξ' : token.list_currency_symbol

  return listPrice ? (
    <BuyModal
      trigger={
        <Button
          colorTheme="primary"
          branded
          className="h-full w-full"
          onClick={(e) => {
            openBuyModal()
            trackButtonClicked(TrackedButtonNames.buyNow, {
              token: token,
              customProperties: {
                Location: 'Project Token Grid Item'
              }
            })
            e.preventDefault()
          }}
        >
          {`Buy for ${listPrice.toString()} ${currencySymbol}`}
        </Button>
      }
      token={getReservoirTokenId(token.id)}
      openState={[
        buyModalOpen,
        (value: boolean | ((prev: boolean) => boolean)) => {
          const newValue = typeof value === 'boolean' ? value : value(buyModalOpen)

          if (newValue) {
            openBuyModal()
            return
          } else {
            closeBuyModal()
          }
        }
      ]}
      onConnectWallet={() => {
        return
      }}
    />
  ) : (
    <BidModal
      tokenId={token.token_id}
      oracleEnabled
      chainId={env.chain.id}
      collectionId={getReservoirCollectionId(token.contract_address, token.project_id)}
      trigger={
        <Button
          colorTheme="secondary"
          branded
          className="h-full w-full"
          onClick={(e) => {
            openBidModal()
            trackButtonClicked(TrackedButtonNames.makeOffer, {
              token: token,
              customProperties: {
                Location: 'Project Token Grid Item'
              }
            })
            e.preventDefault()
          }}
        >
          Make Offer
        </Button>
      }
      openState={[
        bidModalOpen,
        (value: boolean | ((prev: boolean) => boolean)) => {
          const newValue = typeof value === 'boolean' ? value : value(bidModalOpen)

          if (newValue) {
            openBidModal()
            return
          } else {
            closeBidModal()
          }
        }
      ]}
    />
  )
}

const SecondaryOwnerActions = ({
  listPrice,
  token,
  tokenRoute
}: {
  listPrice: number | null
  token: SoloViewerTokenNoProjectFragment
  tokenRoute: string
}) => {
  const { listModalOpen, openListingModal, closeListingModal } = useSecondaryOwnerModalState()

  return listPrice ? (
    <AllPurposeLink
      href={tokenRoute}
      className={cn(buttonVariants({ colorTheme: 'secondary', branded: true }), 'h-full w-full')}
      onClick={() => {
        trackButtonClicked(TrackedButtonNames.editListing, {
          token: token,
          customProperties: {
            Location: 'Project Token Grid Item'
          }
        })
      }}
    >
      Edit Listing
    </AllPurposeLink>
  ) : (
    <ListModal
      trigger={
        <Button
          colorTheme="secondary"
          branded
          className="h-full w-full"
          onClick={(e) => {
            openListingModal()
            trackButtonClicked(TrackedButtonNames.createListing, {
              token: token,
              customProperties: {
                Location: 'Project Token Grid Item'
              }
            })
            e.preventDefault()
          }}
        >
          Create Listing
        </Button>
      }
      oracleEnabled
      tokenId={token.token_id}
      chainId={env.chain.id}
      collectionId={getReservoirCollectionId(token.contract_address, token.project_id)}
      openState={[
        listModalOpen,
        (value: boolean | ((prev: boolean) => boolean)) => {
          const newValue = typeof value === 'boolean' ? value : value(listModalOpen)

          if (newValue) {
            openListingModal()
            return
          } else {
            closeListingModal()
          }
        }
      ]}
    />
  )
}

const AnimatedAllPurposeLink = motion.create(AllPurposeLink)

export const TokenGridVideo = ({
  videoSrc,
  imageSrc,
  blurHash,
  aspectRatio
}: {
  videoSrc: string
  imageSrc: string
  blurHash: string
  aspectRatio: number
}): React.ReactElement | null => {
  const [loading, setIsLoading] = useState(true)
  const [showVideo, setShowVideo] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play()
    }
    setShowVideo(true)
  }

  const handlePause = () => {
    if (videoRef.current) {
      videoRef.current.pause()
      videoRef.current.currentTime = 0
    }
    setShowVideo(false)
  }

  return (
    <div
      className="relative w-full"
      onMouseEnter={() => {
        handlePlay()
      }}
      onMouseLeave={() => {
        handlePause()
      }}
    >
      <video
        ref={videoRef}
        className={`shadow-artwork absolute inset-0 h-full w-full ${!loading && showVideo ? 'block' : 'hidden'} object-cover`}
        onLoadStart={() => setIsLoading(true)}
        onLoadedMetadata={() => setIsLoading(false)}
        onCanPlay={() => {
          setIsLoading(false)
        }}
        onCanPlayThrough={() => setIsLoading(false)}
        controls={false}
        preload="metadata"
        loop
        muted
      >
        <source src={videoSrc} />
      </video>
      <ImageWithCustomLoader
        className={`shadow-artwork relative h-full w-full object-cover ${!loading && showVideo ? 'opacity-0' : 'opacity-100'}`}
        src={imageSrc}
        blurHash={blurHash}
        style={{
          objectFit: 'contain',
          objectPosition: 'center',
          width: '100%',
          height: 'auto',
          aspectRatio: aspectRatio
        }}
        priority
        alt={`video placeholder image`}
        sizes="33vw"
        width={0}
        height={0}
      />
    </div>
  )
}
